import { useState, useMemo, useEffect } from 'react';
import clsx from 'clsx';
import CounterModal from './CounterModal';
import WineModal from './modals/WineModal';
import { PosterProvider, usePoster } from './PosterContext';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation'
import { getVoteStatus, getPtp, postPtp } from 'src/api';
import { useUser } from 'src/context';

const HERO_BODY_SOURCE = "https://storage.googleapis.com/escape-bar-firebase.appspot.com/static/goty2024/hero-body.png"
const HERO_FRONT_SOURCE = "https://storage.googleapis.com/escape-bar-firebase.appspot.com/static/goty2024/hero-front.png"
const HERO_FRONT_LEFT_SOURCE = "https://storage.googleapis.com/escape-bar-firebase.appspot.com/static/goty2024/hero-front-l.png"
const HERO_FRONT_RIGHT_SOURCE = "https://storage.googleapis.com/escape-bar-firebase.appspot.com/static/goty2024/hero-front-r.png"
const HERO_TITLE_SOURCE = "https://storage.googleapis.com/escape-bar-firebase.appspot.com/static/goty2024/hero-title.png"

const HeroContent = () => {
  const [counterModalOpen, setCounterModalOpen] = useState(false);
  const [wineModalOpen, setWineModalOpen] = useState(false);
  const [wineVisible, setWineVisible] = useState(false);

  const { openPosterModal, openDoorModal, isGameEnabled } = usePoster();

  const { isLoadingUser, isLogin } = useUser();

  const { data: voteStatus } = useSWR(`goty2024-voteStatus`, getVoteStatus);

  const { data: ptpData, isLoading: ptpLoading, mutate: ptpMutate } = useSWR(isLogin ? `goty2024-ptp` : null, getPtp);

  const { trigger, isMutating } = useSWRMutation(
    isLogin ? `goty2024-ptp` : null, 
    (url, { arg }) => postPtp(arg),
    {
      onSuccess: (res) => {
        ptpMutate();
      },
    }
  );

  const ptpStep = ptpData?.data?.step || 0;
  const isOpening = voteStatus?.data?.event === "OPEN_SECOND";

  const isWineVisible = useMemo(() => {
    const now = new Date();
    const currentHour = now.getHours();

    return isOpening && ptpStep === 3 && (currentHour >= 20 || currentHour < 4);
  }, [ptpStep, isOpening]);

  const isDoorEnabled = isOpening && ptpStep >= 4;

  useEffect(() => {
    if (isWineVisible) {
      setWineVisible(true);
    }
  }, [isWineVisible]);

  const handleDoorClick = () => {
    if (isDoorEnabled) {
      openDoorModal();
    }
  }

  const handleCounterClick = () => {
    setCounterModalOpen(true);
  }

  const handleCloseCounterModal = () => {
    setCounterModalOpen(false);
  }

  const handleWineClick = () => {
    if (!isGameEnabled) return;
    setWineModalOpen(true);
    setWineVisible(false);
    trigger({
      behavior: "GET_WINE",
    });
  }

  const handleCloseWineModal = () => {
    setWineModalOpen(false);
  }

  return (
    <section 
      id="hero" 
      className='relative bg-[url("https://storage.googleapis.com/escape-bar-firebase.appspot.com/static/goty2024/hero-bg.png")] bg-cover bg-center bg-no-repeat h-[calc(100vh-120px)] md:h-[calc(100vh-46px)] overflow-hidden'
    >
      <CounterModal isOpen={counterModalOpen} onClose={handleCloseCounterModal} />
      <WineModal isOpen={wineModalOpen} onClose={handleCloseWineModal} />
      <img 
        id="hero-title" 
        src={HERO_TITLE_SOURCE} 
        className='absolute right-8 top-8 w-8 md:w-20 object-contain z-10' 
        style={{
          animation: 'stamp 0.6s ease-out 1.5s both'
        }}
      />
      <div className='absolute bottom-32 md:top-24 left-1/2 -translate-x-1/2 w-full md:w-[768px] lg:w-[1024px] flex items-end justify-center'>
        <img 
          src={HERO_BODY_SOURCE} 
          alt="金逃大戲院" 
          className="object-contain" 
        />

        <div 
          id='hero-door'
          className={clsx('absolute left-1/2 -translate-x-1/2 bottom-0 w-1/5 md:w-32 lg:w-40 h-1/4 md:h-32 lg:h-40', {
            'cursor-pointer': isDoorEnabled
          })} 
          onClick={() => handleDoorClick()} 
        />
        
        <div 
          id='hero-counter'
          className='absolute right-1/4 md:right-48 lg:right-64 bottom-0 w-12 md:w-20 lg:w-28 h-1/4 md:h-32 lg:h-40 cursor-pointer' 
          onClick={() => handleCounterClick()} 
        />

        <div 
          id='hero-poster-1'
          className='absolute left-[4%] md:left-[2rem] lg:left-[2.5rem] bottom-[30%] md:bottom-36 lg:bottom-44 h-[17%] md:h-32 lg:h-40 w-[7%] md:w-16 lg:w-20 cursor-pointer' 
          onClick={() => openPosterModal(1)} 
        />
        <div
          id='hero-poster-2'
          className='absolute left-[15%] md:left-[7rem] lg:left-[9rem] bottom-[30%] md:bottom-36 lg:bottom-44 h-[22%] md:h-32 lg:h-48 w-[8%] md:w-16 lg:w-24 cursor-pointer'
          onClick={() => openPosterModal(2 , { isVariant: true })}
        />
        <div
          id='hero-poster-3'
          className='absolute left-[27%] md:left-[13rem] lg:left-[17rem] bottom-[33%] md:bottom-40 lg:bottom-44 h-[22%] md:h-32 lg:h-48 w-[9%] md:w-16 lg:w-24 cursor-pointer'
          onClick={() => openPosterModal(3)}
        />
        <div
          id='hero-poster-4'
          className='absolute left-[40%] md:left-[19rem] lg:left-[25rem] bottom-[35%] md:bottom-44 lg:bottom-56 h-[22%] md:h-32 lg:h-40 w-[9%] md:w-20 lg:w-24 cursor-pointer'
          onClick={() => openPosterModal(4)}
        />
        <div
          id='hero-poster-5'
          className='absolute right-[40%] md:right-[19rem] lg:right-[25rem] bottom-[35%] md:bottom-44 lg:bottom-56 h-[22%] md:h-32 lg:h-40 w-[9%] md:w-20 lg:w-24 cursor-pointer'
          onClick={() => openPosterModal(5)}
        />
        <div
          id='hero-poster-6'
          className='absolute right-[27%] md:right-[13rem] lg:right-[17rem] bottom-[33%] md:bottom-40 lg:bottom-44 h-[22%] md:h-32 lg:h-48 w-[9%] md:w-16 lg:w-24 cursor-pointer'
          onClick={() => openPosterModal(6)}
        />
        <div
          id='hero-poster-7'
          className='absolute right-[15%] md:right-[7rem] lg:right-[9rem] bottom-[30%] md:bottom-36 lg:bottom-44 h-[22%] md:h-32 lg:h-48 w-[8%] md:w-16 lg:w-24 cursor-pointer'
          onClick={() => openPosterModal(7)}
        />
        <div
          id='hero-poster-8'
          className='absolute right-[4%] md:right-[2rem] lg:right-[2.5rem] bottom-[30%] md:bottom-36 lg:bottom-44 h-[17%] md:h-32 lg:h-40 w-[7%] md:w-16 lg:w-20 cursor-pointer'
          onClick={() => openPosterModal(8)}
        />
      </div>
      <img 
        id="hero-front" 
        src={HERO_FRONT_SOURCE} 
        className="absolute bottom-0 left-1/2 w-full -translate-x-1/2 max-w-5xl z-20 object-contain pointer-events-none" 
        style={{
          animation: 'slideIn 0.8s ease-out 0.7s both'
        }}
      />
      <img
        id='hero-wine'
        src='/img/goty2024/game-wine.PNG'
        className={clsx('absolute bottom-8 left-0 md:bottom-12 lg:bottom-24 md:left-4 lg:left-8 w-4 md:w-6 transform rotate-45 object-contain z-10', {
          'cursor-pointer': isGameEnabled && wineVisible,
          'opacity-0': !isGameEnabled || !wineVisible
        })}
        onClick={() => handleWineClick()}
      />
      <img 
        id="hero-front-left" 
        src={HERO_FRONT_LEFT_SOURCE} 
        className="absolute bottom-0 left-0 w-24 md:w-48 lg:w-80 z-10 object-contain" 
      />
      <img 
        id="hero-front-right" 
        src={HERO_FRONT_RIGHT_SOURCE} 
        className="absolute bottom-0 right-0 w-24 md:w-48 lg:w-80 object-contain" 
      />
      <style jsx>{`
        @keyframes slideIn {
          from {
            transform: translate(-50%, 100%);
          }
          to {
            transform: translate(-50%, 0);
          }
        }
        
        @keyframes stamp {
          0% {
            transform: translate(100%, -100%) scale(1.2);
            opacity: 0;
          }
          50% {
            transform: translate(10%, 10%) scale(0.9);
            opacity: 1;
          }
          75% {
            transform: translate(-5%, -5%) scale(1.05);
          }
          100% {
            transform: translate(0, 0) scale(1);
            opacity: 1;
          }
        }
      `}</style>
    </section>
  )
}

const HeroSection = () => {
  return (
    <PosterProvider>
      <HeroContent />
    </PosterProvider>
  )
}

export default HeroSection;
